import type {
  CmsPage,
  ComplianceLegalReference,
  ComplianceThreshold,
} from '../dataProvider';

/**
 * Admin-side helpers for the US compliance settings surface (D-settings-s4-us-compliance,
 * Phase 9 S4), the last and highest-consequence Settings surface. US-ONLY: unlike the S1
 * config there is no region mapping, every row is market 'us'. Holds the tax-report
 * threshold fold (the LIVE connection) and the legal-reference soft-validation (the S3
 * warn-not-block posture). The state set itself is CONTENT-OWNED data seeded in seed.ts,
 * NOT a const here, so states are added as rows as their laws come live.
 */

/** The legal-document roles S4 holds a CMS reference for, in display order. */
export const LEGAL_REFERENCE_KEYS = ['privacy', 'terms'] as const;
export type LegalReferenceKey = (typeof LEGAL_REFERENCE_KEYS)[number];

/** Display label for a legal-reference role (falls back to the key itself). */
export const legalReferenceLabel = (key: string): string =>
  key === 'privacy'
    ? 'US privacy policy'
    : key === 'terms'
      ? 'US terms of service'
      : key;

/**
 * The per-year threshold map the tax report reads, built from the config rows. One row
 * per year, so this is a straight fold. The CALLER merges this over the statutory
 * THRESHOLD_BY_YEAR so a year absent from config falls back to the constant, never blank.
 */
export const thresholdMapFromRows = (
  rows: readonly ComplianceThreshold[],
): Record<number, number> => {
  const map: Record<number, number> = {};
  for (const row of rows) map[row.year] = row.thresholdUsd;
  return map;
};

/** The slugs of the CMS pages that are kind 'legal' (the pages a reference may point at). */
export const legalSlugSet = (cmsPages: readonly CmsPage[]): Set<string> =>
  new Set(cmsPages.filter((p) => p.kind === 'legal').map((p) => p.slug));

/**
 * Whether a slug resolves to a CMS legal page (kind 'legal'). The surface WARNS live when
 * a slug does NOT (the S3 subset-validation posture, soft not blocking): the CMS owns the
 * document, S4 only points at it, so an unresolved slug is a warning not a block.
 */
export const isLegalSlug = (
  slug: string,
  cmsPages: readonly CmsPage[],
): boolean => legalSlugSet(cmsPages).has(slug.trim());

/**
 * The saved references that do NOT resolve to a CMS legal page (missing slug, or a page
 * that is not kind 'legal'). An empty result means every reference resolves cleanly.
 */
export const invalidLegalReferences = (
  references: readonly ComplianceLegalReference[],
  cmsPages: readonly CmsPage[],
): ComplianceLegalReference[] => {
  const legal = legalSlugSet(cmsPages);
  return references.filter((r) => !legal.has(r.cmsSlug));
};
